// Senior-level main.ts - лаконичная точка входа
import { initializeLogger, logger } from '@/engine/logging';
import { mount } from 'svelte';
import App from './App.svelte';

// Импорт инициализаторов и сервисов
import { CoreServiceInitializer } from './engine/CoreServiceInitializer';
import { BootstrapUtils } from './bootstrap/BootstrapUtils';
import { GameAssetBootstrapper } from './game/assets/GameAssetBootstrapper';
import { HMRManager } from './engine/debug/HMRManager';
import { gameHMRConfig } from './game/config/GameHMRConfig';
import { gameSceneConfig } from './game/config/GameSceneConfig';
import { Game, GameConfig } from './engine/Game';
import { loadingProgressService } from './engine/loading/LoadingProgressService';
import { setAppMode } from '@/stores/ui/appState';

// Стили и утилиты
import './styles/theme.css';
import './utils/safeZoneTest';

// ==== Глобальные ссылки (Минимально необходимые) ====
let gameInstance: Game | null = null;
let svelteApp: ReturnType<typeof mount> | null = null;
let bootstrapPromise: Promise<void> | null = null;

// HMR: Инициализация через универсальный HMRManager
if (import.meta.hot) {
  HMRManager.initialize(gameHMRConfig);
  HMRManager.enable({
    getGameInstance: () => gameInstance,
    teardown,
    bootstrap,
  });
}

// ---- Конфиг игры ----
const gameConfig: GameConfig = {
  enableDebugOverlay: import.meta.env.DEV,
  enableFPSLimit: true,
  targetFPS: 60,
  enableAudio: true,
  enableEffects: true,
};

async function initGame() {
  try {
    // 1. Инициализация Core (логирование, ошибки, события, настройки)
    loadingProgressService.startStep('core');
    initializeLogger();
    await CoreServiceInitializer.initializeBaseSystems();
    loadingProgressService.completeStep('core');

    // 2. Ожидание и проверка Canvas (Вся логика retry/app-ready вынесена)
    loadingProgressService.startStep('canvas');
    const renderTarget = await BootstrapUtils.waitForCanvasWithFallback('pixi-canvas');
    if (!renderTarget) {
      throw new Error('Не удалось найти целевой Canvas для рендеринга.');
    }
    loadingProgressService.completeStep('canvas');

    // 3. Постинициализация сервисов и ассетов (Регистрация, Загрузка)
    loadingProgressService.startStep('assets', 'Loading Game Assets...');
    await GameAssetBootstrapper.loadAssetsAndInitializeFactories();
    loadingProgressService.completeStep('assets');

    // 4. Загрузка текстур (отдельный шаг)
    loadingProgressService.startStep('textures', 'Loading Textures...');
    // Текстуры уже загружены в GameAssetBootstrapper, но отмечаем как отдельный шаг
    loadingProgressService.completeStep('textures');

    // 5. Создание и инициализация Игры (после загрузки ассетов)
    loadingProgressService.startStep('game', 'Loading Game Engine...');
    logger.info('🎮 Инициализация игрового движка...', { source: 'main' });
    gameInstance = new Game(gameConfig); // Без стартовой сцены - будет создана через SceneManager
    await gameInstance.initialize();

    // Регистрируем Game в ServiceRegistry для доступа из UI
    CoreServiceInitializer.registerGameServices(gameInstance);

    loadingProgressService.completeStep('game');

    // 6. Восстановление HMR состояния сразу после инициализации игры (пока менеджеры доступны)
    if (import.meta.hot) {
      const { HMRStateManager } = await import('./engine/debug/HMRStateManager');
      if (HMRStateManager.hasSnapshot()) {
        logger.info('🔄 Restoring HMR state...', { source: 'main' });
        await HMRStateManager.restoreSnapshot(gameInstance);
      }
    }

    // 7. Настройка режимного переключения сцен через SceneManager
    loadingProgressService.startStep('ui', 'Preparing Interface...');
    logger.info('🎬 Setting up scene manager...', { source: 'main' });
    const sceneManager = gameInstance.getSceneManager();
    sceneManager.setManagers(gameInstance.getManagers());
    sceneManager.initializeModeSwitching(gameSceneConfig);
    // Синхронизируем стор с defaultMode, чтобы первая подписка не уводила не туда
    setAppMode(gameSceneConfig.defaultMode as any);

    // Создаем и запускаем стартовую сцену
    logger.info('🎬 Creating start scene...', { source: 'main' });
    const startScene = await sceneManager.createStartScene();
    if (startScene) {
      startScene.initialize(gameInstance.getManagers());
      sceneManager.switch(startScene);
      logger.info('✅ Start scene created and switched', { source: 'main' });
    } else {
      logger.error('❌ Failed to create start scene', new Error('Start scene creation failed'), {
        source: 'main',
      });
    }

    // Включаем режимное переключение сцен
    sceneManager.enableModeSwitching();

    // Запускаем игру
    logger.info('🎮 Starting game...', { source: 'main' });
    gameInstance.startGame();

    logger.info('✅ UI step completed', { source: 'main' });
    loadingProgressService.completeStep('ui');

    logger.info('🎯 Completing final step...', { source: 'main' });
    loadingProgressService.completeStep('complete');

    logger.info('🚀 Приложение полностью запущено.', { source: 'main' });
  } catch (err) {
    logger.error('❌ Критическая ошибка при инициализации игры:', err as Error);
    // CRITICAL: Здесь можно добавить fallback UI для отображения ошибки
  }
}

function teardown() {
  if (!gameInstance) return;

  logger.info('🔥 Teardown: Stopping and disposing resources.', { source: 'main' });

  // Отключение режимного переключения сцен
  const sceneManager = gameInstance.getSceneManager();
  sceneManager.disableModeSwitching();

  // Деструктуризация Game и сервисов
  CoreServiceInitializer.cleanup(gameInstance);

  // Уничтожение Svelte App
  svelteApp?.destroy?.();
  svelteApp = null;

  gameInstance = null;
  logger.info('✅ Teardown complete.', { source: 'main' });
}

async function bootstrap() {
  if (bootstrapPromise) return bootstrapPromise;

  const run = async () => {
    // Монтирование Svelte (должно произойти до ожидания Canvas)
    if (!svelteApp) {
      const target = document.getElementById('app');
      if (!target) throw new Error('#app root element is missing');
      svelteApp = mount(App, { target });
    }
    await initGame();
  };

  bootstrapPromise = run()
    .catch(error => {
      logger.error('❌ Ошибка загрузки (Bootstrap failed)', error as Error);
      throw error;
    })
    .finally(() => {
      bootstrapPromise = null;
    });

  return bootstrapPromise;
}

// Запуск
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => void bootstrap(), { once: true });
} else {
  void bootstrap();
}

export default svelteApp;
